/**
 * Cart Drawer Recommendations Updater
 *
 * Updates the cart drawer's "You May Also Like" recommendations when the cart
 * changes (add/remove via quick-add, product buy buttons, or quantity changes).
 * Listens for cart update events and refreshes when the drawer opens.
 *
 * Set window.CART_RECS_DEBUG = true in console to see debug logs.
 */
(function () {
  'use strict';

  var REFRESH_DELAY_MS = 500;
  var COOLDOWN_MS = 2000;
  var refreshTimeout = null;
  var lastRefreshTime = 0;
  var refreshInProgress = false;
  var quantityChangeInProgress = false;

  function debug() {
    if (window.CART_RECS_DEBUG !== false && console && console.log) {
      var args = ['[CartRecs]'].concat(Array.prototype.slice.call(arguments));
      console.log.apply(console, args);
    }
  }

  function getCartElement() {
    return document.querySelector('cart-element[data-view="drawer"]') ||
           document.querySelector('[data-view="cart-drawer"] cart-element');
  }

  function getDrawerSide() {
    var container = document.querySelector('[data-view="cart-drawer"]');
    return container ? container.closest('.drawer--side') : null;
  }

  function updateCartCount() {
    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var count = cart.item_count || 0;
        var text = 'Cart ' + count + ' ' + (count === 1 ? 'item' : 'items');
        var el = document.getElementById('cart-total-text');
        if (el) {
          el.textContent = text;
        }
        var countEls = document.querySelectorAll('[data-cart-count]');
        countEls.forEach(function (e) { e.setAttribute('data-cart-count', count); });
      })
      .catch(function () {});
  }

  function refreshCartDrawerRecommendations() {
    if (refreshInProgress) {
      debug('SKIP: refresh already in progress');
      return Promise.resolve();
    }
    debug('refreshCartDrawerRecommendations() called');
    var cartElement = getCartElement();
    if (!cartElement) {
      debug('SKIP: cart-element not found in drawer');
      return Promise.resolve();
    }
    debug('cart-element found', cartElement);
    refreshInProgress = true;

    // Fetch full cart page and extract footer (section_id on /cart returns 404 on some stores)
    var cartUrl = '/cart';
    debug('fetching:', cartUrl);

    return fetch(cartUrl, {
      method: 'GET',
      headers: { Accept: 'text/html' }
    })
      .then(function (res) {
        debug('fetch status:', res.status, res.url);
        if (!res.ok) {
          throw new Error('HTTP ' + res.status);
        }
        return res.text();
      })
      .then(function (html) {
        debug('fetch ok, html length:', html && html.length);
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var newCartElement = doc.querySelector('cart-element');
        if (!newCartElement) {
          debug('SKIP: no cart-element in response HTML');
          return;
        }
        debug('newCartElement found in response');

        var newFooter = newCartElement.querySelector('.cart--footer');
        var oldFooter = cartElement.querySelector('.cart--footer');
        debug('newFooter:', !!newFooter, 'oldFooter:', !!oldFooter);
        if (newFooter && oldFooter) {
          oldFooter.innerHTML = newFooter.innerHTML;
          debug('footer replaced OK');
        } else {
          debug('SKIP: cannot replace footer (missing new or old)');
        }

        // Don't set count from fetched HTML (can be cached and show 0). Use updateCartCount() only.
        var hasItems = newCartElement.getAttribute('data-has-items');
        if (hasItems !== null) {
          cartElement.setAttribute('data-has-items', hasItems);
        }

        if (typeof window.initCartAjax === 'function') {
          window.initCartAjax();
        }
        if (typeof window.updateCartButtonStates === 'function') {
          window.updateCartButtonStates();
        }
        if (typeof window.updateShippingBg === 'function') {
          window.updateShippingBg();
        }

        if (typeof window.trigger === 'function') {
          try {
            window.trigger('cart:recommendations-updated');
          } catch (e) {}
        }
        lastRefreshTime = Date.now();
        updateCartCount();
        debug('refresh done');
      })
      .catch(function (err) {
        console.warn('[CartRecs] refresh failed:', err);
        debug('fetch/replace error:', err.message || err);
        lastRefreshTime = Date.now();
      })
      .then(function () {
        refreshInProgress = false;
      });
  }

  function scheduleRefresh(skipIfQuantityChange) {
    if (skipIfQuantityChange && quantityChangeInProgress) {
      debug('scheduleRefresh() skipped (quantity change only)');
      quantityChangeInProgress = false;
      return;
    }
    debug('scheduleRefresh()');
    if (refreshTimeout) clearTimeout(refreshTimeout);
    refreshTimeout = setTimeout(function () {
      refreshTimeout = null;
      updateCartCount();
      var now = Date.now();
      if (refreshInProgress) {
        debug('skip refresh (already in progress)');
        return;
      }
      if (now - lastRefreshTime < COOLDOWN_MS) {
        debug('skip refresh (cooldown)');
        return;
      }
      debug('running delayed refresh now');
      refreshCartDrawerRecommendations();
    }, REFRESH_DELAY_MS);
  }

  function onCartUpdated(e) {
    debug('cart:updated event', e ? e.target : 'no target');
    scheduleRefresh();
  }

  function init() {
    var cartEl = getCartElement();
    var drawerSide = getDrawerSide();
    debug('init: cartElement=', !!cartEl, 'sectionId=', cartEl ? cartEl.getAttribute('data-section-id') : 'n/a', 'drawerSide=', !!drawerSide);

    document.addEventListener('cart:updated', onCartUpdated);
    window.addEventListener('cart:updated', onCartUpdated);
    document.body.addEventListener('cart:updated', onCartUpdated);
    debug('listeners added for cart:updated');

    if (drawerSide && typeof MutationObserver !== 'undefined') {
      var observer = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].attributeName === 'aria-expanded' &&
              drawerSide.getAttribute('aria-expanded') === 'true') {
            debug('drawer opened (aria-expanded=true)');
            scheduleRefresh();
            break;
          }
        }
      });
      observer.observe(drawerSide, { attributes: true, attributeFilter: ['aria-expanded'] });
      debug('MutationObserver attached on drawer');
    } else {
      debug('drawerSide or MutationObserver missing');
    }

    // When cart items change (delete, quantity change) in the drawer, refresh recommendations
    if (cartEl && typeof MutationObserver !== 'undefined') {
      var cartBody = cartEl.querySelector('.cart--body');
      if (cartBody) {
        var bodyObserver = new MutationObserver(function () {
          debug('cart--body changed (item added/removed/quantity)');
          scheduleRefresh(true); // skip refresh if this was a quantity change only
        });
        bodyObserver.observe(cartBody, { childList: true, subtree: true });
        debug('MutationObserver attached on cart--body');
      }
    }

    // When add-to-cart is clicked inside the drawer (e.g. from recommendations), refresh after add
    document.body.addEventListener('click', function (e) {
      var drawer = document.querySelector('[data-view="cart-drawer"]');
      if (!drawer || !drawer.contains(e.target)) return;
      if (e.target.closest('.ajax-cart-add-btn') || e.target.closest('.product--quick-add--button')) {
        debug('add-to-cart clicked in drawer');
        scheduleRefresh();
      }
      // Remove item: refresh recommendations
      if (e.target.closest('.quantity-selector--remove')) {
        debug('remove clicked in drawer');
        scheduleRefresh();
      }
      // Quantity +/- only: update count, no full recommendations refresh (avoids delay/reload)
      if (e.target.closest('.quantity-selector--minus') || e.target.closest('.quantity-selector--plus') || e.target.closest('.cart--item--quantity')) {
        quantityChangeInProgress = true;
        setTimeout(function () { quantityChangeInProgress = false; }, 1500);
        setTimeout(updateCartCount, 400);
      }
    }, true);

    // Fallback: when cart--body is replaced entirely (theme may swap the node), re-observe and refresh
    var cartElForObserve = getCartElement();
    if (cartElForObserve && typeof MutationObserver !== 'undefined') {
      var cartContainerObserver = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            if (added[j].nodeType === 1 && added[j].classList && added[j].classList.contains('cart--body')) {
              debug('cart--body node replaced');
              scheduleRefresh(true);
              return;
            }
          }
        }
      });
      cartContainerObserver.observe(cartElForObserve, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
      updateCartCount();
    });
  } else {
    init();
    updateCartCount();
  }

  window.refreshCartDrawerRecommendations = refreshCartDrawerRecommendations;
  window.updateCartCount = updateCartCount;
  debug('CartRecs loaded. Set window.CART_RECS_DEBUG = false to hide logs.');
})();